import React, { FC, useRef, useEffect } from "react";
import { marked } from "marked";
import { Asset, Entry } from "contentful";
import CTA, { CTAProps } from "../CTA";
import {
  Figure,
  Picture,
  Image,
  ButtonContainer,
  Caption,
  NavigationContainer,
  FenderSkinnyContainer,
  FenderSkinnyLeft,
  FenderSkinnyRight,
} from "./styles";

export interface ImageWithTextProps extends Entry {
  fields: {
    altText?: string;
    backgroundAsset?: Asset;
    backgroundColor?: string;
    backgroundPosition: string;
    backgroundRepeat: string;
    backgroundSize: string;
    centerAllContent: boolean;
    contentMaxWidth?: number;
    ctas?: CTAProps[];
    imageMaxWidth?: number;
    layout: string;
    mainImage: Asset;
    mobileBackgroundAsset?: Asset;
    mobileImage: Asset;
    removeTextPadding: boolean;
    textContent?: string;
    textPadding?: string;
    theme: string;
    useCardStyling: boolean;
    useTransparentBackground: boolean;
  };
  brand: string;
  parentProps?: any;
}

const ImageWithText: FC<ImageWithTextProps> = ({
  fields: {
    altText,
    backgroundAsset,
    backgroundColor,
    backgroundPosition = 'center center',
    backgroundRepeat = 'repeat',
    backgroundSize = 'auto',
    centerAllContent,
    contentMaxWidth,
    ctas,
    imageMaxWidth,
    layout,
    mainImage,
    mobileBackgroundAsset,
    mobileImage,
    removeTextPadding = false,
    textContent,
    textPadding = 'Large',
    theme,
    useCardStyling,
    useTransparentBackground,
  },
  brand,
  parentProps,
}) => {
  const topLevelRef = useRef<HTMLDivElement>(null);
  const pictureRef = useRef<HTMLElement>(null);
  const captionRef = useRef<HTMLElement>(null);
  const isInPLPGrid = (parentProps && parentProps.isContentInGrid) || false;
  const customNavigation = (parentProps && parentProps.navigation) || null;

  const setPictureHeight = () => {
    if (layout.includes('wide') && pictureRef.current && captionRef.current) {
      if (window.innerWidth >= 768) {
        pictureRef.current.style.height = `${captionRef.current.clientHeight}px`;
      } else {
        pictureRef.current.style.height = 'auto';
      }
    }
  };

  useEffect(() => {
    if (
      layout.includes('wide') &&
      topLevelRef.current &&
      topLevelRef.current.checkVisibility()
    ) {
      setPictureHeight();
    }
  });

  useEffect(() => {
    setPictureHeight();

    window.addEventListener('resize', setPictureHeight);
    return () => window.removeEventListener('resize', setPictureHeight);
  }, []);

  // The div makes targeting easier in parent components (e.g. Grid, Carousel)
  return (
    <div ref={topLevelRef}>
      <Figure
        $backgroundColor={backgroundColor}
        $backgroundPosition={backgroundPosition}
        $backgroundRepeat={backgroundRepeat}
        $backgroundSize={backgroundSize}
        $desktopBackgroundUrl={
          backgroundAsset ? `https:${backgroundAsset.fields.file?.url}` : undefined
        }
        $mobileBackgroundUrl={
          mobileBackgroundAsset
            ? `https:${mobileBackgroundAsset.fields.file?.url}`
            : undefined
        }
        $center={centerAllContent}
        $isCard={useCardStyling}
        $layout={layout}
        $theme={theme}
        $isInPLPGrid={isInPLPGrid}
        $useTransparentBackground={useTransparentBackground}
      >
        {layout == "skinny-banner" ? (
          <FenderSkinnyContainer $center={centerAllContent}>
            <FenderSkinnyLeft>
              <Picture $layout={layout} ref={pictureRef}>
                <Image
                  alt={altText}
                  $maxWidth={imageMaxWidth}
                  $hide={mobileImage ? "small" : ""}
                  src={`https:${mainImage.fields.file?.url}`}
                />
                {mobileImage && (
                  <Image
                    alt={altText}
                    $hide="large"
                    $maxWidth={imageMaxWidth}
                    src={`https:${mobileImage.fields.file?.url}`}
                  />
                )}
              </Picture>
              {textContent && (
                <Caption
                  $brand={brand}
                  $layout={layout}
                  $maxWidth={contentMaxWidth}
                  $noPadding={removeTextPadding}
                  ref={captionRef}
                  $textPadding={textPadding}
                  carouselStyle={parentProps?.carouselStyle}
                >
                  {parentProps?.carouselStyle === "Product Highlight" && (
                    <NavigationContainer>
                      <div className="custom-fraction-pagination">
                        {parentProps.currentSlide < 10
                          ? `0${parentProps.currentSlide}`
                          : parentProps.currentSlide}{" "}
                        /{" "}
                        {parentProps.totalSlides < 10
                          ? `0${parentProps.totalSlides}`
                          : parentProps.totalSlides}
                      </div>
                      <div className="custom-product-highlight-navigation">
                        {customNavigation}
                      </div>
                    </NavigationContainer>
                  )}
                  <div
                    dangerouslySetInnerHTML={{
                      __html: marked.parse(textContent),
                    }}
                  />
                </Caption>
              )}
            </FenderSkinnyLeft>
            {ctas && (
              <FenderSkinnyRight>
                <ButtonContainer $center={centerAllContent} $layout={layout}>
                  {ctas.map((cta) => (
                    <CTA key={cta.sys.id} {...cta} brand={brand} />
                  ))}
                </ButtonContainer>
              </FenderSkinnyRight>
            )}
          </FenderSkinnyContainer>
        ) : (
          <>
            <Picture $layout={layout} ref={pictureRef}>
              <Image
                alt={altText}
                $maxWidth={imageMaxWidth}
                $hide={mobileImage ? 'small' : ''}
                src={`https:${mainImage.fields.file?.url}`}
              />
              {mobileImage && (
                <Image
                  alt={altText}
                  $hide="large"
                  $maxWidth={imageMaxWidth}
                  src={`https:${mobileImage.fields.file?.url}`}
                />
              )}
            </Picture>
            {textContent && (
              <Caption
                $brand={brand}
                $layout={layout}
                $maxWidth={contentMaxWidth}
                $noPadding={removeTextPadding}
                ref={captionRef}
                $textPadding={textPadding}
                carouselStyle={parentProps?.carouselStyle}
              >
                {parentProps?.carouselStyle === 'Product Highlight' && (
                  <NavigationContainer>
                    <div className="custom-fraction-pagination">
                      {parentProps.currentSlide < 10
                        ? `0${parentProps.currentSlide}`
                        : parentProps.currentSlide}
                      {" / "}
                      {parentProps.totalSlides < 10
                        ? `0${parentProps.totalSlides}`
                        : parentProps.totalSlides}
                    </div>
                    <div className="custom-product-highlight-navigation">
                      {customNavigation}
                    </div>
                  </NavigationContainer>
                )}
                <div
                  dangerouslySetInnerHTML={{
                    __html: marked.parse(textContent),
                  }}
                />
                {ctas && (
                  <ButtonContainer $center={centerAllContent} $layout={layout}>
                    {ctas.map((cta) => (
                      <CTA key={cta.sys.id} {...cta} brand={brand} />
                    ))}
                  </ButtonContainer>
                )}
              </Caption>
            )}
          </>
        )}
      </Figure>
    </div>
  );
};

export default ImageWithText;
